export interface UserProfile {
  userId: string;  // Entra ID
  documentId: string;
  fileName: string;
  fileType: string;
  fileSize: number;
  uploadedAt: Date;
  lastModified: Date;
  originalFile?: Blob;
  originalMarkdown?: string;
  editedMarkdown?: string;
  exportedPDF?: Blob;
  profilePictureUrl?: string;  // New field
  metadata?: {
    processingStatus?: string;
    errorMessage?: string;
    [key: string]: any;
  };
}

// DAG-004 retention: enhancer profile records contain original CV
// content, edited markdown, exported PDFs, and profile picture URLs.
// Same governed-data rule as the CV redactor — purge stale entries on
// every app init so a returning device doesn't keep them indefinitely.
const RETENTION_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

class ProfileStorageService {
  private dbName = 'ProfileEnhancerDB';
  private dbVersion = 1;
  private storeName = 'profiles';
  private db: IDBDatabase | null = null;

  async init(): Promise<void> {
    await new Promise<void>((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.dbVersion);

      request.onerror = () => {
        console.error('Failed to open IndexedDB:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        console.log('Profile IndexedDB initialized successfully');
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        if (!db.objectStoreNames.contains(this.storeName)) {
          const objectStore = db.createObjectStore(this.storeName, {
            keyPath: 'userId'
          });

          objectStore.createIndex('documentId', 'documentId', { unique: false });
          objectStore.createIndex('fileName', 'fileName', { unique: false });
          objectStore.createIndex('uploadedAt', 'uploadedAt', { unique: false });
          objectStore.createIndex('lastModified', 'lastModified', { unique: false });
        }
      };
    });

    // DAG-004: sweep expired profiles every time the app boots.
    try {
      const purged = await this.purgeExpired();
      if (purged > 0) {
        console.info(`Enhancer: purged ${purged} expired profile(s) from IndexedDB`);
      }
    } catch (error) {
      console.warn('Enhancer: retention sweep failed (non-fatal):', error);
    }
  }

  /** Delete every stored profile whose `lastModified` is older than the TTL.
   *
   * Returns the count purged. Called from ``init`` at startup and
   * available for explicit user-driven purge flows.
   */
  async purgeExpired(now: Date = new Date()): Promise<number> {
    const db = await this.ensureDB();
    const transaction = db.transaction([this.storeName], 'readwrite');
    const store = transaction.objectStore(this.storeName);
    const cutoffMs = now.getTime() - RETENTION_TTL_MS;

    return new Promise((resolve, reject) => {
      const cursorRequest = store.openCursor();
      let purgedCount = 0;
      cursorRequest.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest).result;
        if (!cursor) {
          resolve(purgedCount);
          return;
        }
        const profile: UserProfile = cursor.value;
        const last = profile.lastModified ? new Date(profile.lastModified).getTime() : 0;
        if (last < cutoffMs) {
          cursor.delete();
          purgedCount++;
        }
        cursor.continue();
      };
      cursorRequest.onerror = () => reject(cursorRequest.error);
    });
  }

  private async ensureDB(): Promise<IDBDatabase> {
    if (!this.db) {
      await this.init();
    }
    if (!this.db) {
      throw new Error('Failed to initialize database');
    }
    return this.db;
  }

  async saveProfile(profile: UserProfile): Promise<string> {
    const db = await this.ensureDB();
    const transaction = db.transaction([this.storeName], 'readwrite');
    const store = transaction.objectStore(this.storeName);

    return new Promise((resolve, reject) => {
      // Implement upsert logic - update if exists, insert if new
      profile.lastModified = new Date();
      
      const request = store.put(profile);
      request.onsuccess = () => resolve(profile.userId);
      request.onerror = () => reject(request.error);
    });
  }

  async getUserProfile(userId: string): Promise<UserProfile | null> {
    const db = await this.ensureDB();
    const transaction = db.transaction([this.storeName], 'readonly');
    const store = transaction.objectStore(this.storeName);

    return new Promise((resolve, reject) => {
      const request = store.get(userId);
      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(request.error);
    });
  }

  async getCurrentUserProfile(): Promise<UserProfile | null> {
    const db = await this.ensureDB();
    const transaction = db.transaction([this.storeName], 'readonly');
    const store = transaction.objectStore(this.storeName);

    return new Promise((resolve, reject) => {
      // Get the first (and should be only) profile
      const request = store.openCursor();
      request.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest).result;
        if (cursor) {
          resolve(cursor.value);
        } else {
          resolve(null);
        }
      };
      request.onerror = () => reject(request.error);
    });
  }

  async updateOriginalMarkdown(userId: string, markdown: string): Promise<void> {
    const db = await this.ensureDB();
    const transaction = db.transaction([this.storeName], 'readwrite');
    const store = transaction.objectStore(this.storeName);

    return new Promise((resolve, reject) => {
      const request = store.get(userId);
      
      request.onsuccess = () => {
        const profile = request.result;
        if (profile) {
          profile.originalMarkdown = markdown;
          profile.lastModified = new Date();
          
          const updateRequest = store.put(profile);
          updateRequest.onsuccess = () => resolve();
          updateRequest.onerror = () => reject(updateRequest.error);
        } else {
          reject(new Error('Profile not found'));
        }
      };
      
      request.onerror = () => reject(request.error);
    });
  }

  async updateEditedMarkdown(userId: string, markdown: string): Promise<void> {
    const db = await this.ensureDB();
    const transaction = db.transaction([this.storeName], 'readwrite');
    const store = transaction.objectStore(this.storeName);

    return new Promise((resolve, reject) => {
      const request = store.get(userId);
      
      request.onsuccess = () => {
        let profile = request.result;
        if (profile) {
          profile.editedMarkdown = markdown;
          profile.lastModified = new Date();
        } else {
          // Create a new profile if it doesn't exist
          profile = {
            userId: userId,
            documentId: `doc_${Date.now()}`,
            fileName: 'profile.md',
            fileType: 'text/markdown',
            fileSize: markdown.length,
            uploadedAt: new Date(),
            lastModified: new Date(),
            editedMarkdown: markdown,
            originalMarkdown: markdown
          };
        }
        
        const updateRequest = store.put(profile);
        updateRequest.onsuccess = () => resolve();
        updateRequest.onerror = () => reject(updateRequest.error);
      };
      
      request.onerror = () => reject(request.error);
    });
  }

  async updateExportedPDF(userId: string, pdfBlob: Blob): Promise<void> {
    const db = await this.ensureDB();
    const transaction = db.transaction([this.storeName], 'readwrite');
    const store = transaction.objectStore(this.storeName);

    return new Promise((resolve, reject) => {
      const request = store.get(userId);
      
      request.onsuccess = () => {
        const profile = request.result;
        if (profile) {
          profile.exportedPDF = pdfBlob;
          profile.lastModified = new Date();
          
          const updateRequest = store.put(profile);
          updateRequest.onsuccess = () => resolve();
          updateRequest.onerror = () => reject(updateRequest.error);
        } else {
          reject(new Error('Profile not found'));
        }
      };
      
      request.onerror = () => reject(request.error);
    });
  }

  async updateProfilePicture(userId: string, pictureUrl: string): Promise<void> {
    const db = await this.ensureDB();
    const transaction = db.transaction([this.storeName], 'readwrite');
    const store = transaction.objectStore(this.storeName);

    return new Promise((resolve, reject) => {
      const request = store.get(userId);
      
      request.onsuccess = () => {
        let profile = request.result;
        if (profile) {
          profile.profilePictureUrl = pictureUrl;
          profile.lastModified = new Date();
        } else {
          // Create a minimal profile if it doesn't exist
          profile = {
            userId: userId,
            documentId: `doc_${Date.now()}`,
            fileName: 'profile.md',
            fileType: 'text/markdown',
            fileSize: 0,
            uploadedAt: new Date(),
            lastModified: new Date(),
            profilePictureUrl: pictureUrl
          };
        }
        
        const updateRequest = store.put(profile);
        updateRequest.onsuccess = () => resolve();
        updateRequest.onerror = () => reject(updateRequest.error);
      };
      
      request.onerror = () => reject(request.error);
    });
  }

  async deleteProfile(userId: string): Promise<void> {
    const db = await this.ensureDB();
    const transaction = db.transaction([this.storeName], 'readwrite');
    const store = transaction.objectStore(this.storeName);

    return new Promise((resolve, reject) => {
      const request = store.delete(userId);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async clearAll(): Promise<void> {
    const db = await this.ensureDB();
    const transaction = db.transaction([this.storeName], 'readwrite');
    const store = transaction.objectStore(this.storeName);

    return new Promise((resolve, reject) => {
      const request = store.clear();
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }
}

export const profileStorageService = new ProfileStorageService();